import { apiClient } from "@/shared/services/api";

export interface SeedlingStagesOverview {
  pendingOrIssued: number;
  inSowing: number;
  germinated: number;
  growing: number;
  readyForDispatch: number;
}

export interface AgroforestryStagesOverview {
  healing: number;
  dispatchReady: number;
}

export interface SaleableInventoryGroup {
  label: string;
  unit: string;
  available: number;
  reserved: number;
  dispatchReady: number;
}

export interface SaleableInventory {
  vegetables: SaleableInventoryGroup;
  agroforestry: SaleableInventoryGroup;
  estimatedSaleValue: number | null;
  estimatedSaleValueUnavailableReason: string | null;
}

export interface GreenhouseUtilization {
  total: number;
  occupied: number;
  available: number;
  occupancyPct: number;
  spaceReleasingAfterDispatchTrays: number;
  spaceRequiredByPendingSowingTrays: number;
}

export interface OrdersPerMonthPoint {
  year: number;
  month: number;
  monthLabel: string;
  orderCount: number;
}

export interface DashboardOverview {
  asOf: string;
  seedlingStages: SeedlingStagesOverview;
  agroforestryStages: AgroforestryStagesOverview;
  saleableInventory: SaleableInventory;
  greenhouseUtilization: GreenhouseUtilization;
  ordersPerMonth: OrdersPerMonthPoint[];
  overgrownSeedlings: number;
  onHoldSeedlings: number;
  activeGreenhouses: number;
  occupiedGreenhouses: number;
}

export interface ManagementProductSales {
  productCategory: string;
  productName: string;
  pieceCount: number;
  trayEquivalent: number;
  salesAmount: number;
  settlementAllocatedAmount?: number | null;
  tradeReceivableAmount?: number | null;
  settlementAllocationBasis?: string | null;
}

export interface ManagementMonthlySales {
  month: number;
  monthLabel: string;
  currentYear: number;
  currentYearSales: number;
  previousYear: number;
  previousYearSales: number;
}

export interface ManagementPeriodValue {
  pieceCount?: number | null;
  trayEquivalent?: number | null;
  monetaryValue?: number | null;
  eventCount: number;
  availability: number;
  note?: string | null;
}

export interface ManagementActivity {
  code: string;
  label: string;
  daily: ManagementPeriodValue;
  weekly: ManagementPeriodValue;
  monthly: ManagementPeriodValue;
  yearToDate: ManagementPeriodValue;
  note?: string | null;
}

export interface ManagementDashboard {
  asOf: string;
  businessTimezone: string;
  currency: string;
  dailyProductSales: ManagementProductSales[];
  weeklySales: { grossSalesAmount: number; pieceCount: number; trayEquivalent: number };
  monthlySales: { grossSalesAmount: number; pieceCount: number; trayEquivalent: number };
  monthlyComparison: ManagementMonthlySales[];
  dailyActivities: ManagementActivity[];
  dataQualityWarnings: string[];
  accounting?: ManagementAccountingSnapshot | null;
}

export interface ManagementAccountingSection {
  asOf: string;
  businessTimezone: string;
  currency: string;
  accounting: ManagementAccountingSnapshot | null;
}

export interface ManagementProductSettlementSection {
  asOf: string;
  businessTimezone: string;
  currency: string;
  products: ManagementProductSales[];
}

export interface ManagementInvoicedSummarySection {
  asOf: string;
  businessTimezone: string;
  currency: string;
  weeklySales: ManagementDashboard["weeklySales"];
  monthlySales: ManagementDashboard["monthlySales"];
}

export interface ManagementInvoicedTrendSection {
  asOf: string;
  businessTimezone: string;
  currency: string;
  monthlyComparison: ManagementMonthlySales[];
}

export interface ManagementActivitiesSection {
  asOf: string;
  businessTimezone: string;
  activities: ManagementActivity[];
  dataQualityWarnings: string[];
}

export interface ManagementAccountingSnapshot {
  dailySalesByPaymentMethod: Array<{ paymentMethod: string; amount: number }> | null;
  recognizedRevenueToday: number | null;
  postedCashReceivedToday: number | null;
  postedOpenInvoiceAmountFromToday: number;
  postedSettledAmountOnOpenInvoicesFromToday: number;
  postedInvoiceOutstandingFromToday: number;
  postedInvoiceOutstanding: number;
  postedAccountsReceivableControl: number | null;
  accountsReceivableVariance: number | null;
  operationalUnpostedInvoiceOutstanding: number;
  pendingPaymentApprovalAmount: number;
  pendingPaymentApprovalCount: number;
  asOfExclusiveUtc: string;
  definitionVersion: string;
}

interface ApiResponse<T> { data: T }

const unwrap = <T>(data: T | ApiResponse<T>): T =>
  typeof data === "object" && data !== null && "data" in data ? data.data : data;

export const dashboardApi = {
  getOverview: async (): Promise<DashboardOverview> => {
    const { data } = await apiClient.get<DashboardOverview | ApiResponse<DashboardOverview>>("/dashboard/overview");
    return unwrap(data);
  },
  getManagement: async (companyId: string): Promise<ManagementDashboard> => {
    const { data } = await apiClient.get<ManagementDashboard | ApiResponse<ManagementDashboard>>(
      "/dashboard/management", { params: { companyId } }
    );
    return unwrap(data);
  },
  getAccountingPosition: async (companyId: string): Promise<ManagementAccountingSection> => {
    const { data } = await apiClient.get<ManagementAccountingSection | ApiResponse<ManagementAccountingSection>>(
      "/dashboard/management/accounting-position", { params: { companyId } }
    );
    return unwrap(data);
  },
  getProductSettlement: async (companyId: string): Promise<ManagementProductSettlementSection> => {
    const { data } = await apiClient.get<ManagementProductSettlementSection | ApiResponse<ManagementProductSettlementSection>>(
      "/dashboard/management/product-settlement", { params: { companyId } }
    );
    return unwrap(data);
  },
  getInvoicedSummary: async (companyId: string): Promise<ManagementInvoicedSummarySection> => {
    const { data } = await apiClient.get<ManagementInvoicedSummarySection | ApiResponse<ManagementInvoicedSummarySection>>(
      "/dashboard/management/invoiced-summary", { params: { companyId } }
    );
    return unwrap(data);
  },
  getInvoicedTrend: async (companyId: string): Promise<ManagementInvoicedTrendSection> => {
    const { data } = await apiClient.get<ManagementInvoicedTrendSection | ApiResponse<ManagementInvoicedTrendSection>>(
      "/dashboard/management/invoiced-trend", { params: { companyId } }
    );
    return unwrap(data);
  },
  getActivities: async (companyId?: string): Promise<ManagementActivitiesSection> => {
    const { data } = await apiClient.get<ManagementActivitiesSection | ApiResponse<ManagementActivitiesSection>>(
      "/dashboard/management/activities", companyId ? { params: { companyId } } : undefined
    );
    return unwrap(data);
  },
  refreshManagement: async (companyId: string): Promise<void> => {
    await apiClient.post("/dashboard/management/refresh", undefined, { params: { companyId } });
  },
};
